import React, { useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import {
	PageTitle,
	OfferSpendListTable,
	OfferSpendCreateModal,
	OfferSpendEditModal,
	OfferSpendEditNotesModal,
	OfferSpendRemoveModal,
	OfferSpendHistoryListModal,
	IconPlus,
	ToolPanel,
	UiPagination,
	ESortOrder,
	getAccountIsFiredBadge
} from '../components'
import { Filters, FiltersButtonIcon } from '../components/filters'
import { useOfferSpendList, useOfferSpendFiltersPage, useOfferSpendSortPage } from '../hooks'
import * as fetchs from '../fetchs'
import * as lib from '../lib'
import * as state from '../states'
import { IFetchItemsSetup, INoFetch, IOfferDropDown, IOfferSpend, IAccountBuyer, ERoleAccess } from '../types'
import { TAKE, MENU } from '../constants'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../types/lang'


export const OfferSpendPage = (): React.JSX.Element => {
	const { t } = useTranslation()
	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)
	const tokens = useAtomValue(state.tokensAtom)
	const access = tokens?.access ?? null

	const profile = useAtomValue(state.profileAtom)
	const $access = new lib.Access(profile ?? null)

	const isBuyer = useAtomValue(state.isBuyerAtom)

	const location = useLocation()
	const href = lib.pageHref(location, TAKE)
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()

	const page = lib.skipSearchParams(searchParams.get('page')) ?? 0
	const take = lib.takeSearchParams(searchParams.get('take')) ?? TAKE
	const skip = (take === 'all') ? 0 : page > 0 ? page * take : 0
	const buyer = isBuyer ?? lib.buyerIdSearchParams(searchParams.get('buyer') ?? '')

	const buyerItems = useAtomValue(state.accountBuyersAtom)
	const currentBuyer = buyerItems?.find(item => item.id === buyer) ?? null
	const currentBuyerIsFired = currentBuyer?.isFired
	const currentBuyerTeamReport = currentBuyer?.role?.teamReport
	const keitaroLogin: string = currentBuyer?.extra?.keitaroLogin ?? ''
	const currentBuyerFormat = `{${currentBuyerTeamReport}} - [id: ${currentBuyer?.id}] ${currentBuyer?.auth?.login ?? currentBuyer?.email ?? '?'} (${keitaroLogin ?? ''})`
	const isOfferSpendPageIndex = !buyer
	const pageTitle = t(MENU.offerSpend.title)
	const pageTitleFormat = `${pageTitle}${!isOfferSpendPageIndex ? ': ' + currentBuyerFormat : ''}`

	const [offers, setOffers] = React.useState<Array<IOfferDropDown> | null>([])
	const [errorOffers, setErrorOffers] = React.useState<INoFetch | null>(null)

	React.useEffect(() => {
		const controller = new AbortController()
		const signal = controller.signal

		const call = async () => {
			try {
				setFetchIsLoading(state.turnOnLoader)
				const [
					offers
				] = await Promise.allSettled([
					fetchs.listOfferDropDown(
						lib.filterSetup([]),
						lib.sortSetup([['id', 'desc']]),
						lib.skipSetup(0),
						lib.takeSetup('all'),
						signal,
						access
					)
				])
				// listOfferDropDown
				if (offers?.status === 'fulfilled' && offers?.value?.items) {
					setErrorOffers(null)
					setOffers(offers.value.items)
				} else if (offers?.status === 'rejected') {
					setErrorOffers(offers.reason as INoFetch)
					setOffers(null)
				}
			} finally {
				setFetchIsLoading(state.turnOffLoader)
			}
		}
		call()
		return () => {
			controller.abort()
		}
	}, [
		access,
		setFetchIsLoading,
		setOffers,
		setErrorOffers
	])

	const {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		spendFiltersConfig,
		defaultFiltersValue,
		initialFiltersValue,
		clearChildFilters,
		setUserFilters
	} = useOfferSpendFiltersPage(buyer)

	//TODO: check sort output var type ?
	const {
		// sort: sortTmp,
		sortBy,
		setSortBy,
		order,
		setOrder
	} = useOfferSpendSortPage(buyer)

	const setup: IFetchItemsSetup = {
		filter,
		sort: order !== ESortOrder.unset ? [[sortBy, order]] : [],
		skip,
		take
	}
	const { error, items, total, warmUp } = useOfferSpendList(setup, send)

	const [createOfferSpend, setCreateOfferSpend] = useState<boolean>(false)
	const [editOfferSpend, setEditOfferSpend] = useState<IOfferSpend | null>(null)
	const [editNotesOfferSpend, setEditNotesOfferSpend] = useState<IOfferSpend | null>(null)
	const [removeOfferSpend, setRemoveOfferSpend] = useState<IOfferSpend | null>(null)
	const [viewOfferSpendHistory, setViewOfferSpendHistory] = useState<IOfferSpend | null>(null)

	const onCreate = (e: React.MouseEvent) => {
		e.preventDefault()
		setCreateOfferSpend(true)
	}
	const onEdit = (item: IOfferSpend, e: React.MouseEvent) => {
		e.preventDefault()
		setEditOfferSpend(item)
	}
	const onEditNotes = (item: IOfferSpend, e: React.MouseEvent) => {
		e.preventDefault()
		setEditNotesOfferSpend(item)
	}
	const onRemove = (item: IOfferSpend, e: React.MouseEvent) => {
		e.preventDefault()
		setRemoveOfferSpend(item)
	}

	const onViewOfferSpendHistory = (item: IOfferSpend, e: React.MouseEvent) => {
		e.preventDefault()
		setViewOfferSpendHistory(item)
	}

	return (
		<>
			<PageTitle title={
				<>
					<span>{pageTitleFormat}</span>
					{!!currentBuyerIsFired ? getAccountIsFiredBadge(currentBuyerIsFired, t) : null}
				</>
			}/>

			<div className="card rounded-3 border-0 mb-24">
				<div className="card-body p-25">
					{/* remove CREATE button for 'account' main page (without pre-selected group) */}
					<div className="d-flex align-items-center justify-content-between pb-20 border-bottom border-color">
						<FiltersButtonIcon
							activeFiltersCount={activeFiltersCount}
							filterFillFactor={filterFillFactor}
							onClick={() => setIsFiltersPanelOpen((val) => !val)}
						/>

						{
							!!onCreate && !!$access.$(ERoleAccess.OfferSpendCreate)
								// && !isOfferSpendPageIndex
								? (
									<button
										type="button"
										className="btn btn-primary d-flex align-items-center"
										onClick={onCreate}
									>
										<IconPlus/>
										<span className="d-none d-lg-block">{`${t(ALL_LANG_KEY.offer_spend_page.btn_add_new)}`}</span>
									</button>
								)
								: null
						}

					</div>

					<Filters
						config={spendFiltersConfig}
						initialFiltersValue={initialFiltersValue}
						resetFiltersValue={defaultFiltersValue}
						isAutoModeWithDebounce={false}
						isFiltersPanelOpen={isFiltersPanelOpen}
						isFiltersToClear={clearChildFilters}
						setFiltersCB={(value) => {
							//console.log('page.RolePage::setFiltersCB::', { value })
							setUserFilters(value)
						}}
					/>

					<OfferSpendListTable
						items={items}
						skip={skip}
						sortBy={sortBy}
						setSortBy={setSortBy}
						order={order}
						setOrder={setOrder}
						onEdit={onEdit}
						onEditNotes={onEditNotes}
						onRemove={onRemove}
						onViewHistory={onViewOfferSpendHistory}
					/>

					<ToolPanel flow="<-">
						<UiPagination
							classesToAdd={{
								wrapper: 'mt-25'
							}}
							page={page}
							take={take}
							total={total}
							href={(page: number) => href(page).tail}
						/>
					</ToolPanel>
				</div>
			</div>

			{
				!!createOfferSpend && !!$access.$(ERoleAccess.OfferSpendCreate) && <OfferSpendCreateModal
					show={createOfferSpend}
					accounts={buyerItems}
					offers={offers}
					onClose={() => setCreateOfferSpend(false)}
					onSave={() => {
						setCreateOfferSpend(false)
						navigate(href(0).tail)
						warmUp()
					}}
				/>
			}

			{
				!!editOfferSpend && !!$access.$(ERoleAccess.OfferSpendModify) && <OfferSpendEditModal
					item={editOfferSpend}
					show={!!editOfferSpend}
					accounts={buyerItems}
					offers={offers}
					onClose={() => setEditOfferSpend(null)}
					onSave={() => {
						setEditOfferSpend(null)
						navigate(href(0).tail)
						warmUp()
					}}
				/>
			}

			{
				!!editNotesOfferSpend && !!$access.$(ERoleAccess.OfferSpendList) && <OfferSpendEditNotesModal
					isViewMode={!$access.$(ERoleAccess.OfferSpendModify)}
					item={editNotesOfferSpend}
					show={!!editNotesOfferSpend}
					onClose={() => setEditNotesOfferSpend(null)}
					onSave={() => {
						setEditNotesOfferSpend(null)
						//navigate(href(0).tail)
						warmUp()
					}}
				/>
			}

			{
				!!removeOfferSpend && !!$access.$(ERoleAccess.OfferSpendRemove) && <OfferSpendRemoveModal
					item={removeOfferSpend}
					show={!!removeOfferSpend}
					onClose={() => setRemoveOfferSpend(null)}
					onSave={() => {
						setRemoveOfferSpend(null)
						navigate(href(0).tail)
						warmUp()
					}}
				/>
			}

			{
				!!viewOfferSpendHistory && !!$access.$(ERoleAccess.OfferSpendList) && <OfferSpendHistoryListModal
					offerSpend={viewOfferSpendHistory}
					show={!!viewOfferSpendHistory}
					take={take}
					onClose={() => {
						setViewOfferSpendHistory(null)
						// navigate(href(0).tail)
						warmUp()
					}}
				/>
			}

		</>
	)
}
